import React from 'react';
import { useTheme } from '../context/ThemeContext';

// Sun icon for light mode
const SunIcon = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z"
    />
  </svg>
);

// Moon icon for dark mode
const MoonIcon = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z"
    />
  </svg>
);

// Computer/System icon
const SystemIcon = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 17.25v1.007a3 3 0 0 1-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0 1 15 18.257V17.25m6-12V15a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 15V5.25m18 0A2.25 2.25 0 0 0 18.75 3H5.25A2.25 2.25 0 0 0 3 5.25m18 0V12a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 12V5.25"
    />
  </svg>
);

// Simple toggle button for header
export const ThemeToggleButton = () => {
  const { isDark, toggleTheme, mounted } = useTheme();

  // Prevent hydration mismatch
  if (!mounted) {
    return (
      <button
        className="w-9 h-9 rounded-lg flex items-center justify-center opacity-50 cursor-wait"
        aria-label="Loading theme"
        disabled
      >
        <div className="w-5 h-5 animate-pulse rounded-full bg-current opacity-20" />
      </button>
    );
  }

  return (
    <button
      onClick={toggleTheme}
      className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/10 transition-all duration-200 group"
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {isDark ? (
        <SunIcon className="w-5 h-5 text-silver/60 group-hover:text-pure-white transition-colors" />
      ) : (
        <MoonIcon className="w-5 h-5 text-slate-500 group-hover:text-slate-700 transition-colors" />
      )}
    </button>
  );
};

// Full theme selector with options for settings page
export const ThemeSelector = () => {
  const { theme, setTheme, THEMES, mounted } = useTheme();

  if (!mounted) {
    return (
      <div className="flex gap-2">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="flex-1 h-12 rounded-lg animate-pulse bg-current opacity-10"
          />
        ))}
      </div>
    );
  }

  const options = [
    { value: THEMES.LIGHT, label: 'Light', Icon: SunIcon },
    { value: THEMES.DARK, label: 'Dark', Icon: MoonIcon },
    { value: THEMES.SYSTEM, label: 'System', Icon: SystemIcon },
  ];

  return (
    <div className="flex gap-2">
      {options.map(({ value, label, Icon }) => (
        <button
          key={value}
          onClick={() => setTheme(value)}
          className={`
            flex-1 flex flex-col items-center gap-2 px-4 py-3 rounded-xl
            border transition-all duration-200
            ${
              theme === value
                ? 'border-blue-500 bg-blue-500/10 text-blue-500'
                : 'border-transparent hover:border-current/20 text-current/60 hover:text-current'
            }
          `}
          aria-pressed={theme === value}
          aria-label={`Use ${label} theme`}
        >
          <Icon className="w-5 h-5" />
          <span className="font-mono text-xs uppercase tracking-widest">
            {label}
          </span>
        </button>
      ))}
    </div>
  );
};

export default ThemeToggleButton;
